import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BloodTest from "../assets/BloodTest.png";
import { API_CONFIG } from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Sparkles, ArrowRight, AlertCircle, Brain, Zap } from 'lucide-react';
import { classifyScore, formatScoreDisplay } from '../utils/wellness';
import { adaptStarScoresForCompletion } from '../logic/insights/starAdapters';
import StarRadarChart from '../components/StarRadarChart';
import { formatDate } from '../utils/dateFormatter';

interface AssessmentData {
  id: string;
  type: 'physical' | 'depression' | 'anxiety' | 'stress' | 'emotional';
  score?: number;
  status: 'completed' | 'in-progress' | 'not-started';
  completedAt?: string;
  level?: string;
  incompleteCategories?: string[];
}

const WellnessPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Handlers for card clicks
  // All three handlers below now direct the user to the DASS-21 assessment (auto-select DASS-21 category)
  const handleDepressionWellbeing = () => {
    navigate('/panel/learn/emotio-psycho-rational-assessment', { state: { dass21: true } });
  };

  const handleAnxietyWellbeing = () => {
    navigate('/panel/learn/emotio-psycho-rational-assessment', { state: { dass21: true } });
  };

  const handleStressWellbeing = () => {
    navigate('/panel/learn/emotio-psycho-rational-assessment', { state: { dass21: true } });
  };
  
  // Make cache keys user-specific to prevent data leakage between users
  const WELLNESS_CACHE_KEY = user?.id ? `lifepsensor_wellness_assessments_cache_${user.id}` : 'lifepsensor_wellness_assessments_cache';
  const STAR_CACHE_KEY = user?.id ? `lifepsensor_star_wellbeing_cache_${user.id}` : 'lifepsensor_star_wellbeing_cache';
  
  // Check for cache immediately to determine initial loading state
  const cachedData = localStorage.getItem(WELLNESS_CACHE_KEY);
  const cachedStarData = localStorage.getItem(STAR_CACHE_KEY);
  const hasCache = !!cachedData;

  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [isLoading, setIsLoading] = useState(!hasCache);
  const [starAssessment, setStarAssessment] = useState<any>(() => {
    // Initialize starAssessment from cache immediately
    if (cachedStarData) {
      try {
        return JSON.parse(cachedStarData);
      } catch (error) {

        return null;
      }
    }
    return null;
  });
  const [chartMode, setChartMode] = useState<'filled' | 'slashed'>('filled');


  // Always fetch fresh data first, clear cache to ensure we get latest data
  useEffect(() => {
    // Clear wellness cache to force fresh data fetch
    localStorage.removeItem(WELLNESS_CACHE_KEY);

    const fetchFreshData = async () => {
      try {
        await fetchAssessments();
      } catch (error) {

        // If fresh fetch fails, try to load from cache as fallback (though we just cleared it)
        const cached = localStorage.getItem(WELLNESS_CACHE_KEY);
        if (cached) {
          try {
            const cachedAssessments = JSON.parse(cached);
            setAssessments(cachedAssessments);
          } catch (cacheError) {

            localStorage.removeItem(WELLNESS_CACHE_KEY);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFreshData();
  }, []);

  // Save to cache whenever assessments change
  useEffect(() => {
    if (assessments.length > 0 && !isLoading) {
      localStorage.setItem(WELLNESS_CACHE_KEY, JSON.stringify(assessments));
    }
  }, [assessments, isLoading]);

  // Extract fetchAssessments function to be reusable
  const fetchAssessments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {

        return;
      }

      // Fetch data from physical, emotio-psycho-rational (for DASS subscales), and emotional wellbeing assessments
      const assessmentTypes = [
        { type: 'physical', endpoint: API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH_LATEST },
        { type: 'dass', endpoint: API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL_LATEST },
        { type: 'emotional', endpoint: API_CONFIG.ENDPOINTS.EMOTIONAL_WELLBEING_LATEST }
      ];

      const assessmentPromises = assessmentTypes.map(async ({ type, endpoint }) => {
        try {
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            },
            credentials: 'include',
            cache: 'no-store'
          });

          if (response.ok) {
            const data = await response.json();
            const assessment = data.data;

            if (assessment) {
              // Special handling for DASS subscales (depression, anxiety, stress)
              if (type === 'dass') {
                const scores = assessment.scores;
                const hasDassData = scores?.subscales && scores?.subscales?.depression !== undefined &&
                                  scores?.subscales?.anxiety !== undefined && scores?.subscales?.stress !== undefined;

                if (hasDassData) {
                  // Return separate assessments for each DASS subscale
                  const dassAssessments = [
                    {
                      id: `${assessment._id}-depression`,
                      type: 'depression' as const,
                      score: scores.subscales.depression,
                      status: 'completed' as const,
                      completedAt: assessment.createdAt,
                      level: getDassLevel(scores.subscales.depression, 'depression')
                    },
                    {
                      id: `${assessment._id}-anxiety`,
                      type: 'anxiety' as const,
                      score: scores.subscales.anxiety,
                      status: 'completed' as const,
                      completedAt: assessment.createdAt,
                      level: getDassLevel(scores.subscales.anxiety, 'anxiety')
                    },
                    {
                      id: `${assessment._id}-stress`,
                      type: 'stress' as const,
                      score: scores.subscales.stress,
                      status: 'completed' as const,
                      completedAt: assessment.createdAt,
                      level: getDassLevel(scores.subscales.stress, 'stress')
                    }
                  ];
                  return dassAssessments;
                } else {
                  // No DASS data available, return placeholder assessments
                  return [
                    {
                      id: 'dass-depression-placeholder',
                      type: 'depression' as const,
                      status: 'not-started' as const,
                      score: undefined,
                      completedAt: undefined,
                      level: undefined
                    },
                    {
                      id: 'dass-anxiety-placeholder',
                      type: 'anxiety' as const,
                      status: 'not-started' as const,
                      score: undefined,
                      completedAt: undefined,
                      level: undefined
                    },
                    {
                      id: 'dass-stress-placeholder',
                      type: 'stress' as const,
                      status: 'not-started' as const,
                      score: undefined,
                      completedAt: undefined,
                      level: undefined
                    }
                  ];
                }
              } else {
                // Special handling for physical assessments - calculate from IPAQ-SF and PSQI categories
                if (type === 'physical') {

                  // Get status and categories from assessment
                  const categories = assessment?.scores?.categories || assessment?.assessmentData?.scores?.categories || [];

                  const ipaqCategory = categories.find((c: any) => c.name === 'IPAQ-SF');
                  const psqiCategory = categories.find((c: any) => c.name === 'PSQI');


                  const ipaqCompleted = !!ipaqCategory;
                  const psqiCompleted = !!psqiCategory;
                  const status = (ipaqCompleted || psqiCompleted) ? 'completed' : 'not-started';
                  
                  // Calculate average of rounded IPAQ-SF and PSQI scores
                  let overallScore = 0;
                  const roundedScores = [];
                  
                  if (ipaqCategory && typeof ipaqCategory.score === 'number') {
                    const rounded = Math.round(ipaqCategory.score);
                    console.log('[WellnessPage Physical] IPAQ Score (rounded):', ipaqCategory.score, '->', rounded);
                    roundedScores.push(rounded);
                  }
                  if (psqiCategory && typeof psqiCategory.score === 'number') {
                    const rounded = Math.round(psqiCategory.score);
                    console.log('[WellnessPage Physical] PSQI Score (rounded):', psqiCategory.score, '->', rounded);
                    roundedScores.push(rounded);
                  }

                  if (roundedScores.length > 0) {
                    overallScore = Math.round(roundedScores.reduce((a: number, b: number) => a + b, 0) / roundedScores.length);

                  } else if (assessment?.scores?.overall !== undefined) {
                    // Fallback to backend-calculated overall score if categories not available
                    overallScore = Math.round(assessment.scores.overall);

                  }

                  return {
                    id: assessment._id,
                    type: 'physical' as const,
                    score: overallScore,
                    status: status as 'completed' | 'not-started',
                    completedAt: assessment.completedAt || assessment.createdAt,
                    level: assessment.results?.level,
                    incompleteCategories: status === 'not-started' ? ['IPAQ-SF', 'PSQI'] : undefined
                  };
                } else {
                  // Regular handling for emotional assessments
                  return {
                    id: assessment._id,
                    type: type as 'emotional',
                    score: assessment.scores?.overall,
                    status: (assessment.status === 'completed' ? 'completed' : 'in-progress') as 'completed' | 'in-progress' | 'not-started',
                    completedAt: assessment.completedAt,
                    level: assessment.results?.level
                  };
                }
              }
            }
          } else if (response.status === 404) {
            // No assessment found - return not-started status
            if (type === 'dass') {
              return [
                {
                  id: 'dass-depression-placeholder',
                  type: 'depression' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                },
                {
                  id: 'dass-anxiety-placeholder',
                  type: 'anxiety' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                },
                {
                  id: 'dass-stress-placeholder',
                  type: 'stress' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                }
              ];
            } else {
              if (type === 'physical') {
                return {
                  id: `${type}-placeholder`,
                  type: 'physical' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined,
                  incompleteCategories: ['General Physical Assessment']
                };
              } else {
                return {
                  id: `${type}-placeholder`,
                  type: type as 'emotional',
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                };
              }
            }
          } else {
            // For other errors, treat as not-started
            if (type === 'dass') {
              return [
                {
                  id: 'dass-depression-placeholder',
                  type: 'depression' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                },
                {
                  id: 'dass-anxiety-placeholder',
                  type: 'anxiety' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                },
                {
                  id: 'dass-stress-placeholder',
                  type: 'stress' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                }
              ];
            } else {
              if (type === 'physical') {
                return {
                  id: `${type}-placeholder`,
                  type: 'physical' as const,
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined,
                  incompleteCategories: ['General Physical Assessment']
                };
              } else {
                return {
                  id: `${type}-placeholder`,
                  type: type as 'emotional',
                  status: 'not-started' as const,
                  score: undefined,
                  completedAt: undefined,
                  level: undefined
                };
              }
            }
          }
        } catch (error) {

          return {
            id: `${type}-placeholder`,
            type: type as 'physical' | 'mental' | 'emotional',
            status: 'not-started' as const,
            score: undefined,
            completedAt: undefined,
            level: undefined
          };
        }
      });

      const assessmentResults = await Promise.all(assessmentPromises);
      // Flatten results (DASS returns array, others return single objects)
      const flattenedResults = assessmentResults.flat();
      // Filter out any undefined results
      let validResults = flattenedResults.filter(result => result !== undefined) as AssessmentData[];

      // Check if we have DASS subscale assessments but they're showing as not started - fetch raw DASS data as fallback
      const depressionResult = validResults.find(r => r.type === 'depression');
      const anxietyResult = validResults.find(r => r.type === 'anxiety');
      const stressResult = validResults.find(r => r.type === 'stress');

      const hasIncompleteDass = (depressionResult?.status === 'not-started' ||
                                anxietyResult?.status === 'not-started' ||
                                stressResult?.status === 'not-started');

      if (hasIncompleteDass) {
        try {
          const dassResponse = await fetch(API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL_LATEST, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (dassResponse.ok) {
            const dassData = await dassResponse.json();
            const rawAssessment = dassData.data?.assessment;

            if (rawAssessment && rawAssessment.scores?.subscales) {
              // Update the subscale results with actual scores
              const depressionScore = rawAssessment.scores.subscales.depression;
              const anxietyScore = rawAssessment.scores.subscales.anxiety;
              const stressScore = rawAssessment.scores.subscales.stress;

              validResults = validResults.map(result => {
                if (result.type === 'depression' && depressionScore !== undefined) {
                  return { ...result, score: depressionScore, status: 'completed' as const, completedAt: rawAssessment.createdAt };
                }
                if (result.type === 'anxiety' && anxietyScore !== undefined) {
                  return { ...result, score: anxietyScore, status: 'completed' as const, completedAt: rawAssessment.createdAt };
                }
                if (result.type === 'stress' && stressScore !== undefined) {
                  return { ...result, score: stressScore, status: 'completed' as const, completedAt: rawAssessment.createdAt };
                }
                return result;
              });
            }
          }
        } catch (fallbackError) {

        }
      }

      setAssessments(validResults);

      // Fetch Star of Well-Being assessment data
      try {
        const starResponse = await fetch(API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING_LATEST, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          credentials: 'include',
          cache: 'no-store'
        });

        if (starResponse.ok) {
          const starData = await starResponse.json();
          const starDataToCached = starData.data || null;
          setStarAssessment(starDataToCached);
          // Cache the star data to localStorage
          if (starDataToCached) {
            localStorage.setItem(STAR_CACHE_KEY, JSON.stringify(starDataToCached));
          }
        } else {
          setStarAssessment(null);
        }
      } catch (starError) {
        setStarAssessment(null);
      }
    } catch (error) {
      setAssessments([]);
      setStarAssessment(null);
    } finally {
      setIsLoading(false);
    }
  }, [WELLNESS_CACHE_KEY, STAR_CACHE_KEY]);

  // Fetch assessment data when component mounts
  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  useEffect(() => {
    const handleRefresh = () => {
      if (document.visibilityState === 'visible') {
        fetchAssessments();
      }
    };

    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);

    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
  }, [fetchAssessments]);

  // Separate function to fetch just Star assessment data
  const fetchStarAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const starResponse = await fetch(API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING_LATEST, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (starResponse.ok) {
        const starData = await starResponse.json();
        setStarAssessment(starData.data || null);
      } else {
        setStarAssessment(null);
      }
    } catch (starError) {
      setStarAssessment(null);
    }
  };


  const handleBloodTestInput = () => {
    navigate('/panel/learn/');
  };

  const handlePhysicalWellbeing = () => {
    navigate('/panel/learn/physical-wellness-assessment', { state: { directAssessment: 'General Physical Assessment' } });
  };





  // Helper function to determine DASS level based on subscale scores (inverted scale: higher = better)
  const getDassLevel = (score: number, subscale: 'depression' | 'anxiety' | 'stress'): string => {
    // DASS-21 severity ranges
    if (subscale === 'depression') {
      if (score <= 9) return 'Normal';
      if (score <= 13) return 'Mild';
      if (score <= 20) return 'Moderate';
      if (score <= 27) return 'Severe';
      return 'Extremely Severe';
    }
    if (subscale === 'anxiety') {
      if (score <= 7) return 'Normal';
      if (score <= 9) return 'Mild';
      if (score <= 14) return 'Moderate';
      if (score <= 19) return 'Severe';
      return 'Extremely Severe';
    }
    if (subscale === 'stress') {
      if (score <= 14) return 'Normal';
      if (score <= 18) return 'Mild';
      if (score <= 25) return 'Moderate';
      if (score <= 33) return 'Severe';
      return 'Extremely Severe';
    }
    return 'Unknown';
  };

  // Helper function to get Material Symbols icon names for each Star of Well-Being attribute
  const getAttributeIcon = (key: string): string => {
    const icons: { [key: string]: string } = {
      spiritual: 'self_improvement',
      nutritional: 'restaurant',
      physical: 'fitness_center',
      emotioPsychoRational: 'psychology',
      legendarial: 'history_edu',
      religioTraditioCultural: 'account_balance',
      financial: 'payments',
      environmental: 'nature',
      socioRelational: 'diversity_3',
      occupational: 'work'
    };
    return icons[key] || 'star';
  };

  const getAssessmentDisplay = (assessment: AssessmentData | undefined) => {
    if (!assessment || assessment.status === 'not-started') {
      return {
        level: 'Not Started',
        class: 'wellness__card-risk--none',
        description: 'Take your assessment in the Learn section to get personalized insights.'
      };
    }

    if (assessment.status === 'in-progress') {
      let description = 'Continue your assessment to see your results.';
      if (assessment.incompleteCategories && assessment.incompleteCategories.length > 0) {
        description += ` You haven't answered these categories yet: ${assessment.incompleteCategories.join(', ')}.`;
      }
      return {
        level: 'In Progress',
        class: 'wellness__card-risk--in-progress',
        description: description
      };
    }

    if (assessment.status === 'completed' && assessment.score !== undefined) {
      const dateStr = assessment.completedAt ? formatDate(assessment.completedAt) : 'Recently';
      let description = `Completed on ${dateStr}.`;

      // Special handling for DASS subscales
      if (assessment.type === 'depression' || assessment.type === 'anxiety' || assessment.type === 'stress') {
        // DASS wellness percentage formulas:
        // Each subscale goes from 0 to 42
        // Percentage = (subscaleScore / 42) * 100
        // 0/42 = 0% (no symptoms), 42/42 = 100% (extreme symptoms)
        let wellnessPercentage = Math.round((assessment.score / 42) * 100);

        // DASS-21 standard label mapping
        let severityLevel;
        let riskClass;
        if (assessment.score <= 9) {
          severityLevel = 'Normal';
          riskClass = 'wellness__card-risk--low-risk'; // Green
        } else if (assessment.score <= 13) {
          severityLevel = 'Mild';
          riskClass = 'wellness__card-risk--moderate-risk'; // Orange
        } else if (assessment.score <= 20) {
          severityLevel = 'Moderate';
          riskClass = 'wellness__card-risk--moderate-risk'; // Orange
        } else if (assessment.score <= 27) {
          severityLevel = 'Severe';
          riskClass = 'wellness__card-risk--high-risk'; // Red
        } else {
          severityLevel = 'Extremely Severe';
          riskClass = 'wellness__card-risk--high-risk'; // Red
        }

        return {
          level: `${severityLevel} (${wellnessPercentage}%)`,
          class: riskClass,
          description: description
        };
      }

      // Regular assessment handling
      const scoreClassification = classifyScore(assessment.score);

      // For mental health, show incomplete categories if any
      if (assessment.incompleteCategories && assessment.incompleteCategories.length > 0) {
        description += ` You haven't answered these categories yet: ${assessment.incompleteCategories.join(', ')}.`;
      }

      return {
        level: `${scoreClassification.label} (${formatScoreDisplay(assessment.score)})`,
        class: scoreClassification.className,
        description: description
      };
    }

    return {
      level: 'Unknown',
      class: 'wellness__card-risk--none',
      description: 'Assessment status unclear.'
    };
  };

  if (isLoading) {
    return <LoadingScreen message="Loading wellness data..." fullScreen />;
  }

  return (
    <div className="wellness">
      <div className="wellness__container">
        {/* Main Content */}
        <main className="wellness__main">
          {/* Header Section */}
          <div className="wellness__header">
            <div className="wellness__header-content">
              <div className="wellness__header-text">
                <h2 className="wellness__main-title">Your Well-Being Overview</h2>
              </div>
            </div>
          </div>

          {/* Main Grid Layout */}
          <div className="wellness__grid">
            {/* Left Column - Wellness Cards */}
            <div className="wellness__cards-section">
              <div className="wellness__cards-grid">
                {/* Depression Card (navigates to DASS-21 assessment) */}
                <div className="wellness__card" onClick={handleDepressionWellbeing} style={{ cursor: 'pointer' }}>
                  <div className="wellness__card-header">
                    <h3 className="wellness__card-title">Depression</h3>
                    <div className="wellness__card-icon wellness__card-icon--depression"><AlertCircle aria-hidden="true" /></div>
                  </div>
                  {(() => {
                    const depressionAssessment = assessments.find(a => a.type === 'depression');
                    const display = getAssessmentDisplay(depressionAssessment);

                    // Extract percentage from display level (e.g., "Normal (81%)" -> 81)
                    const percentageMatch = display.level.match(/\((\d+)%\)/);
                    const percentage = percentageMatch ? parseInt(percentageMatch[1]) : 0;
                    const progressValue = percentage / 100;

                    // Determine circle class based on risk class
                    let circleClass = 'wellness__progress-circle--unknown';
                    if (display.class.includes('low-risk')) circleClass = 'wellness__progress-circle--high';
                    else if (display.class.includes('moderate-risk')) circleClass = 'wellness__progress-circle--moderate';
                    else if (display.class.includes('high-risk')) circleClass = 'wellness__progress-circle--low';

                    return (
                      <>
                        <div
                          className={`wellness__progress-circle ${circleClass}`}
                          style={{ '--progress': progressValue } as React.CSSProperties}
                        >
                          <span>{percentage}%</span>
                        </div>
                        <p className={`wellness__card-risk ${display.class}`}>
                          {display.level.replace(/\s*\(\d+%\)/, '').trim()} {/* Remove percentage from text */}
                        </p>
                        <p className="wellness__card-description">
                          {display.description}
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Anxiety Card (navigates to DASS-21 assessment) */}
                <div className="wellness__card" onClick={handleAnxietyWellbeing} style={{ cursor: 'pointer' }}>
                  <div className="wellness__card-header">
                    <h3 className="wellness__card-title">Anxiety</h3>
                    <div className="wellness__card-icon wellness__card-icon--anxiety"><Brain aria-hidden="true" /></div>
                  </div>
                  {(() => {
                    const anxietyAssessment = assessments.find(a => a.type === 'anxiety');
                    const display = getAssessmentDisplay(anxietyAssessment);

                    // Extract percentage from display level (e.g., "Normal (81%)" -> 81)
                    const percentageMatch = display.level.match(/\((\d+)%\)/);
                    const percentage = percentageMatch ? parseInt(percentageMatch[1]) : 0;
                    const progressValue = percentage / 100;

                    // Determine circle class based on risk class
                    let circleClass = 'wellness__progress-circle--unknown';
                    if (display.class.includes('low-risk')) circleClass = 'wellness__progress-circle--high';
                    else if (display.class.includes('moderate-risk')) circleClass = 'wellness__progress-circle--moderate';
                    else if (display.class.includes('high-risk')) circleClass = 'wellness__progress-circle--low';

                    return (
                      <>
                        <div
                          className={`wellness__progress-circle ${circleClass}`}
                          style={{ '--progress': progressValue } as React.CSSProperties}
                        >
                          <span>{percentage}%</span>
                        </div>
                        <p className={`wellness__card-risk ${display.class}`}>
                          {display.level.replace(/\s*\(\d+%\)/, '').trim()} {/* Remove percentage from text */}
                        </p>
                        <p className="wellness__card-description">
                          {display.description}
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Stress Card (navigates to DASS-21 assessment) */}
                <div className="wellness__card" onClick={handleStressWellbeing} style={{ cursor: 'pointer' }}>
                  <div className="wellness__card-header">
                    <h3 className="wellness__card-title">Stress</h3>
                    <div className="wellness__card-icon wellness__card-icon--stress"><Zap aria-hidden="true" /></div>
                  </div>
                  {(() => {
                    const stressAssessment = assessments.find(a => a.type === 'stress');
                    const display = getAssessmentDisplay(stressAssessment);

                    // Extract percentage from display level (e.g., "Normal (81%)" -> 81)
                    const percentageMatch = display.level.match(/\((\d+)%\)/);
                    const percentage = percentageMatch ? parseInt(percentageMatch[1]) : 0;
                    const progressValue = percentage / 100;

                    // Determine circle class based on risk class
                    let circleClass = 'wellness__progress-circle--unknown';
                    if (display.class.includes('low-risk')) circleClass = 'wellness__progress-circle--high';
                    else if (display.class.includes('moderate-risk')) circleClass = 'wellness__progress-circle--moderate';
                    else if (display.class.includes('high-risk')) circleClass = 'wellness__progress-circle--low';

                    return (
                      <>
                        <div
                          className={`wellness__progress-circle ${circleClass}`}
                          style={{ '--progress': progressValue } as React.CSSProperties}
                        >
                          <span>{percentage}%</span>
                        </div>
                        <p className={`wellness__card-risk ${display.class}`}>
                          {display.level.replace(/\s*\(\d+%\)/, '').trim()} {/* Remove percentage from text */}
                        </p>
                        <p className="wellness__card-description">
                          {display.description}
                        </p>
                      </>
                    );
                  })()}
                </div>

                {/* Physical Wellbeing Card */}
                <div className="wellness__card" onClick={handlePhysicalWellbeing} style={{ cursor: 'pointer' }}>
                  <div className="wellness__card-header">
                    <h3 className="wellness__card-title">Physical Wellbeing</h3>
                    <div className="wellness__card-icon wellness__card-icon--physical"><Heart aria-hidden="true" /></div>
                  </div>
                  {(() => {
                    const physicalAssessment = assessments.find(a => a.type === 'physical');
                    const display = getAssessmentDisplay(physicalAssessment);

                    // For physical wellbeing, extract percentage if it exists
                    const percentageMatch = display.level.match(/(\d+)%/);
                    const percentage = percentageMatch ? parseInt(percentageMatch[1]) : 0;
                    const progressValue = percentage / 100;

                    // Determine circle class based on risk class
                    let circleClass = 'wellness__progress-circle--unknown';
                    if (display.class.includes('low-risk')) circleClass = 'wellness__progress-circle--high';
                    else if (display.class.includes('moderate-risk')) circleClass = 'wellness__progress-circle--moderate';
                    else if (display.class.includes('high-risk')) circleClass = 'wellness__progress-circle--low';

                    return (
                      <>
                        <div
                          className={`wellness__progress-circle ${circleClass}`}
                          style={{ '--progress': progressValue } as React.CSSProperties}
                        >
                          <span>{display.level.includes('Not Started') ? '0%' : `${percentage}%`}</span>
                        </div>
                        <p className={`wellness__card-risk ${display.class}`}>
                          {display.level.replace(/\s*\(\d+%\)/, '').trim()} {/* Remove percentage from text */}
                        </p>
                        <p className="wellness__card-description">
                          {display.description}
                        </p>
                      </> 
                    );
                  })()}
                </div>
              </div>
            </div>
            {/* Right Column - Star Chart */}
            <div className="wellness__star-chart">
              <h3 className="wellness__chart-title">Your Star of Well-Being</h3>
              {starAssessment && starAssessment.scores && starAssessment.scores.individualAttributes ? (
                <>
                  <div className="wellness__star-content">
                    {/* Left - Radar Chart */}
                    <div className="wellness__chart-section">
                      {(() => {
                        // Round scores for chart display to match the text display
                        const roundedIndividualAttributes = Object.entries(starAssessment.scores.individualAttributes).reduce((acc, [key, score]) => {
                          acc[key] = Math.round(score as number); // Round to nearest integer (1-4)
                          return acc;
                        }, {} as { [key: string]: number });
                        return (
                          <StarRadarChart
                            scores={adaptStarScoresForCompletion(roundedIndividualAttributes)}
                            chartMode={chartMode}
                            size="full"
                          />
                        );
                      })()}
                      
                      {/* Chart Mode Toggle */}
                      <div className="wellness__chart-controls">
                        <div className="wellness__mode-toggle">
                          <button
                            className={`wellness__mode-btn ${chartMode === 'filled' ? 'active' : ''}`}
                            onClick={() => setChartMode('filled')}
                          >
                            Filled
                          </button>
                          <button
                            className={`wellness__mode-btn ${chartMode === 'slashed' ? 'active' : ''}`}
                            onClick={() => setChartMode('slashed')}
                          >
                            Slashed
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Right - Category Cards */}
                    <div className="wellness__attributes-list">
                      {Object.entries(starAssessment.scores.individualAttributes).map(([key, score]: any, idx: number) => {
                        // Map database keys to display names
                        const attributeNames: { [key: string]: string } = {
                          spiritual: 'Spiritual',
                          nutritional: 'Nutritional',
                          physical: 'Physical',
                          emotioPsychoRational: 'Emotio-Psycho-Rational',
                          legendarial: 'Legendarial',
                          religioTraditioCultural: 'Religio-Traditio-Cultural',
                          financial: 'Financial',
                          environmental: 'Environmental',
                          socioRelational: 'Socio-Relational',
                          occupational: 'Occupational'
                        };

                        const attributeDescriptions: { [key: string]: string } = {
                          spiritual: 'Connection to purpose and values',
                          nutritional: 'Quality of eating habits',
                          physical: 'Physical health and fitness',
                          emotioPsychoRational: 'Emotional and mental well-being',
                          legendarial: 'Life purpose and legacy',
                          religioTraditioCultural: 'Cultural and traditional alignment',
                          financial: 'Financial security and stability',
                          environmental: 'Living environment support',
                          socioRelational: 'Social connections and relationships',
                          occupational: 'Work satisfaction and fulfillment'
                        };

                        const displayName = attributeNames[key] || key;
                        const description = attributeDescriptions[key] || '';
                        const roundedScore = Math.round(score as number); // Round to nearest integer (1-4)
                        const statusClass = roundedScore >= 3.5 ? 'excellent' : roundedScore >= 2.5 ? 'good' : roundedScore >= 1.5 ? 'fair' : 'poor';
                        const statusLabel = roundedScore >= 4 ? 'Excellent' : roundedScore >= 3 ? 'Good' : roundedScore >= 2 ? 'Fair' : 'Poor';
                        
                        return (
                          <div key={idx} className="wellness__attribute-card">
                            <div className="wellness__attribute-top">
                              <div className={`wellness__attribute-icon-wrapper wellness__attribute-icon-wrapper--${key}`}>
                                <span className="material-symbols-outlined">{getAttributeIcon(key)}</span>
                              </div>
                              <h4 className="wellness__attribute-name">{displayName}</h4>
                            </div>
                            <div className="wellness__attribute-bottom">
                              <span className={`wellness__attribute-score`}>{roundedScore}/4</span>
                              <span className={`wellness__attribute-status wellness__attribute-status--${statusClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="wellness__actions">
                    <button
                      className="wellness__details-btn"
                      onClick={() => navigate('/panel/wellness/details')}
                    >
                      Details
                    </button>
                    <button
                      className="wellness__retake-btn"
                      onClick={() => navigate('/panel/wellness/dass')}
                    >
                      Retake
                    </button>
                  </div>
                </>
              ) : (
                <div className="wellness__chart-placeholder">
                  <p>Complete your Star of Well-Being assessment to see your results here.</p>
                  <button
                    className="wellness__chart-action"
                    onClick={() => navigate('/panel/wellness/dass')}
                  >
                    Take Assessment
                  </button>
                </div>
              )}

            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default WellnessPage;




